<script setup>
import { ref, computed, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { useDateLocale } from 'dashboard/composables/useDateLocale';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import MessageFormatter from 'shared/helpers/MessageFormatter';
import {
  isSafeHttpLink,
  formatDocumentLink,
  getDocumentDisplayPath,
} from 'shared/helpers/documentHelper';
import { Launch16, Copy16 } from '@carbon/icons-vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import DrawerFrame from 'dashboard/components-next/polaris/cards/DrawerFrame.vue';
import FoldingGroup from 'dashboard/components-next/polaris/cards/FoldingGroup.vue';
import DetailList from 'dashboard/components-next/polaris/cards/DetailList.vue';
import PaginationFooter from 'dashboard/components-next/polaris/cards/PaginationFooter.vue';
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from 'dashboard/components-next/polaris/ui/item';

// The details of a document (phase 4.3): a drawer, not a dialog — a column beside the list from 1280px, the
// non-modal overlay below (DrawerFrame, R2-X-3). Its parts are sections that open and close (Q1): the summary, the
// content (or the PDF) and the FAQs generated from it. Same data and same requests as the dialog it replaces.
// It stays mounted and `open` drives the frame, so the DrawerFrame's close path runs (focus back to the card that
// opened it); the page keeps the last document while it closes.

const props = defineProps({
  open: { type: Boolean, default: false },
  polarisDocument: { type: Object, default: null },
});
const emit = defineEmits(['close']);
const RESPONSES_PER_PAGE = 25;
const { t } = useI18n();
const store = useStore();
const documentDetails = computed(() => props.polarisDocument);
const showRawContent = ref(false);

const uiFlags = useMapGetter('polarisResponses/getUIFlags');
const responses = useMapGetter('polarisResponses/getRecords');
const meta = useMapGetter('polarisResponses/getMeta');
const isFetching = computed(() => uiFlags.value.fetchingList);
const totalCount = computed(() => meta.value.totalCount || 0);
const currentPage = computed(() => meta.value.page || 1);
const showPaginationFooter = computed(
  () => totalCount.value > RESPONSES_PER_PAGE
);
const documentContent = computed(() => documentDetails.value?.content?.trim());
const documentContentLength = computed(
  () => documentContent.value?.length || 0
);
const isPdf = computed(() => documentDetails.value?.pdf_document);
const displayUrl = computed(() => documentDetails.value?.display_url);
const externalLink = computed(() => documentDetails.value?.external_link);
const sourceHref = computed(() => displayUrl.value || externalLink.value);
const hasSafeLink = computed(() => isSafeHttpLink(sourceHref.value));
const displayLink = computed(() => {
  if (isPdf.value) return formatDocumentLink(externalLink.value);
  return getDocumentDisplayPath(displayUrl.value || externalLink.value);
});
const title = computed(
  () => documentDetails.value?.name || documentDetails.value?.external_link
);
const contentTitle = computed(() =>
  isPdf.value
    ? t('POLARIS.DOCUMENTS.DETAILS.PDF_TAB')
    : t('POLARIS.DOCUMENTS.DETAILS.CONTENT_TAB')
);
const isUnreadableContent = computed(() => {
  if (!documentContent.value) return false;

  const content = documentContent.value;
  const sample = content.slice(0, 2000);
  const characters = Array.from(sample);
  const nonPrintableCharacters = characters.filter(character => {
    const charCode = character.charCodeAt(0);
    return (
      (charCode <= 31 && ![9, 10, 13].includes(charCode)) ||
      (charCode >= 127 && charCode <= 159)
    );
  });
  const nonPrintableRatio =
    nonPrintableCharacters.length / Math.max(characters.length, 1);
  const replacementCharacterRatio =
    characters.filter(character => character === '\uFFFD').length /
    Math.max(characters.length, 1);
  const hasPdfObjectMarkers =
    content.includes(' obj') &&
    content.includes(' endobj') &&
    content.includes(' stream');

  return (
    content.startsWith('%PDF') ||
    hasPdfObjectMarkers ||
    nonPrintableRatio > 0.02 ||
    replacementCharacterRatio > 0.05
  );
});
const formattedDocumentContent = computed(() => {
  if (!documentContent.value || isUnreadableContent.value) return '';

  const formatter = new MessageFormatter(documentContent.value);
  formatter.disableImageRendering();
  return formatter.formattedMessage;
});
const dateLocale = useDateLocale();
const updatedAtLabel = computed(() => {
  if (!documentDetails.value?.updated_at) return null;
  return messageTimestamp(
    documentDetails.value.updated_at,
    'MMM d, yyyy h:mm a',
    dateLocale.value
  );
});
const syncedAtLabel = computed(() => {
  if (!documentDetails.value?.last_synced_at) return null;
  return messageTimestamp(
    documentDetails.value.last_synced_at,
    'MMM d, yyyy h:mm a',
    dateLocale.value
  );
});

const handleClose = () => {
  emit('close');
};

const handleCopyContent = async () => {
  try {
    await copyTextToClipboard(documentContent.value);
    useAlert(t('POLARIS.DOCUMENTS.DETAILS.COPY_SUCCESS'));
  } catch {
    useAlert(t('POLARIS.DOCUMENTS.DETAILS.COPY_ERROR'));
  }
};

const fetchResponses = (page = 1) => {
  return store.dispatch('polarisResponses/get', {
    page,
    assistantId: props.polarisDocument.assistant.id,
    documentId: props.polarisDocument.id,
  });
};

const handlePageChange = page => {
  fetchResponses(page);
};

// The answers are markdown; the item shows them as text (bold markers out).
const plain = text => (text || '').replace(/(\*\*|__)(.+?)\1/g, '$2');

const summary = computed(() => [
  [t('POLARIS.DOCUMENTS.DETAILS.SOURCE'), displayLink.value],
  [t('POLARIS.DOCUMENTS.DETAILS.GENERATED_FAQS'), String(totalCount.value)],
  [
    t('POLARIS.DOCUMENTS.DETAILS.LAST_UPDATED'),
    syncedAtLabel.value ||
      updatedAtLabel.value ||
      t('POLARIS.DOCUMENTS.DETAILS.NOT_AVAILABLE'),
  ],
]);

// A new document (or the same one opened again) fetches its FAQs and starts on the preview.
watch(
  () => [props.open, props.polarisDocument?.id],
  ([isOpen, id]) => {
    if (!isOpen || !id) return;
    showRawContent.value = false;
    fetchResponses();
  },
  { immediate: true }
);
</script>

<template>
  <DrawerFrame
    :open="open && !!polarisDocument"
    :label="title"
    column-class="w-[min(43.875rem,50%)]"
    overlay-class="sm:max-w-[43.875rem]"
    data-document-details
    @close="handleClose"
  >
    <header
      class="grid gap-1 border-0 border-b border-solid border-polaris-border px-6 py-4 pe-12"
    >
      <h2 class="truncate type-polaris-section-title">{{ title }}</h2>
      <p class="type-polaris-body text-polaris-muted-foreground">
        {{ t('POLARIS.DOCUMENTS.DETAILS.DESCRIPTION') }}
      </p>
    </header>
    <div
      class="grid min-h-0 flex-1 content-start gap-6 overflow-y-auto px-6 py-5"
    >
      <FoldingGroup :title="t('POLARIS.PAGES.DOCUMENT_DETAILS.SUMMARY')">
        <DetailList :items="summary" />
        <template v-if="hasSafeLink" #actions>
          <Button variant="outline" size="sm" as-child>
            <a :href="sourceHref" target="_blank" rel="noopener noreferrer">
              <Launch16 aria-hidden="true" />
              {{ t('POLARIS.PAGES.DOCUMENT_DETAILS.OPEN_SOURCE') }}
            </a>
          </Button>
        </template>
      </FoldingGroup>

      <FoldingGroup
        :title="contentTitle"
        :description="
          documentContent && !isPdf
            ? t('POLARIS.DOCUMENTS.DETAILS.CHARACTER_COUNT', {
                count: documentContentLength.toLocaleString(),
              })
            : ''
        "
      >
        <p v-if="isPdf" class="type-polaris-body text-polaris-muted-foreground">
          {{ t('POLARIS.DOCUMENTS.DETAILS.PDF_DESCRIPTION') }}
        </p>
        <template v-else-if="documentContent">
          <p
            v-if="isUnreadableContent && !showRawContent"
            class="rounded-polaris-lg border border-dashed border-polaris-border p-4 type-polaris-body text-polaris-muted-foreground"
          >
            {{ t('POLARIS.DOCUMENTS.DETAILS.UNREADABLE_CONTENT') }}
          </p>
          <div
            v-else
            class="max-h-[26rem] overflow-y-auto rounded-polaris-lg border border-solid border-polaris-border bg-polaris-muted p-4"
          >
            <pre
              v-if="showRawContent || isUnreadableContent"
              class="m-0 whitespace-pre-wrap break-words type-polaris-caption text-polaris-foreground"
            ><code>{{ documentContent }}</code></pre>
            <div
              v-else
              v-dompurify-html="formattedDocumentContent"
              class="prose prose-sm max-w-none break-words text-polaris-foreground prose-p:my-2 prose-headings:mb-2 prose-headings:mt-4 prose-headings:text-polaris-foreground prose-a:text-polaris-foreground prose-strong:text-polaris-foreground prose-ul:my-2 prose-ol:my-2 prose-li:my-1 prose-img:hidden"
            />
          </div>
        </template>
        <p
          v-else
          class="rounded-polaris-lg border border-dashed border-polaris-border p-4 type-polaris-body text-polaris-muted-foreground"
        >
          {{ t('POLARIS.DOCUMENTS.DETAILS.EMPTY_CONTENT') }}
        </p>
        <template v-if="documentContent && !isPdf" #actions>
          <Button
            variant="outline"
            size="sm"
            @click="showRawContent = !showRawContent"
          >
            {{
              showRawContent
                ? t('POLARIS.DOCUMENTS.DETAILS.VIEW_PREVIEW')
                : t('POLARIS.DOCUMENTS.DETAILS.VIEW_RAW')
            }}
          </Button>
          <Button variant="outline" size="sm" @click="handleCopyContent">
            <Copy16 aria-hidden="true" />
            {{ t('POLARIS.DOCUMENTS.DETAILS.COPY_CONTENT') }}
          </Button>
        </template>
      </FoldingGroup>

      <FoldingGroup
        :title="t('POLARIS.DOCUMENTS.RELATED_RESPONSES.TITLE')"
        :count="totalCount"
        data-document-faqs
      >
        <div
          v-if="isFetching"
          class="flex justify-center py-6 text-polaris-muted-foreground"
        >
          <Spinner />
        </div>
        <template v-else-if="responses.length">
          <Item
            v-for="response in responses"
            :key="response.id"
            variant="outline"
            size="sm"
          >
            <ItemContent class="min-w-0">
              <ItemTitle class="break-words">{{ response.question }}</ItemTitle>
              <ItemDescription class="line-clamp-3 break-words">
                {{ plain(response.answer) }}
              </ItemDescription>
            </ItemContent>
          </Item>
        </template>
        <p
          v-else
          class="rounded-polaris-lg border border-dashed border-polaris-border p-4 type-polaris-body text-polaris-muted-foreground"
        >
          {{ t('POLARIS.DOCUMENTS.RELATED_RESPONSES.EMPTY') }}
        </p>
        <PaginationFooter
          v-if="showPaginationFooter"
          :page="currentPage"
          :total="totalCount"
          :page-size="RESPONSES_PER_PAGE"
          @update:page="handlePageChange"
        />
      </FoldingGroup>
    </div>
  </DrawerFrame>
</template>
